// SCR-STAFF-955 — اطلاعیه گروهی خدماتی (US-MSG-081؛ AP-MSG-01): گیرندگان فقط نوبت‌داران فعال بازه (حداکثر ۳۱ روز) در شعبه فعال؛
// پیش‌نمایش تعداد گیرنده و طول پیامک؛ هدف Transactional و محتوای تبلیغاتی ممنوع (PHX-MSG-0203 — BR-MSG-081)؛ فهرست اطلاعیه‌های ارسال‌شده.
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type BulkNoticeRequest, type BulkNoticeView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  TextFieldRHF,
  toBusinessDate,
  useDisplaySettings,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { WriteNotice } from '../settings/common';
import { localToUtc, msgKeys, SCR_STAFF_955 } from './common';

interface BulkForm {
  date: string;
  from: string;
  to: string;
  text: string;
}

export function BulkNoticesPage() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('msg.send.bulk_notice');
  const display = useDisplaySettings();
  const queryClient = useQueryClient();
  const form = useForm<BulkForm>({
    defaultValues: { date: toBusinessDate(new Date()), from: '00:00', to: '23:59', text: '' },
  });
  const text = form.watch('text') ?? '';
  const [done, setDone] = useState<BulkNoticeView | null>(null);
  const notices = useQuery({
    queryKey: msgKeys.bulk(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/bulk-notices')),
  });

  const request = (): BulkNoticeRequest => {
    const { date, from, to } = form.getValues();
    return {
      from: localToUtc(date, from, display.timeZone),
      to: localToUtc(date, to, display.timeZone),
      branchId: session?.activeBranchId ?? null,
      providerId: null,
      text: text.trim() || null,
    };
  };
  const preview = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/messaging/bulk-notices:preview', { body: request() })),
  });
  const send = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/messaging/bulk-notices', { body: request() })),
    onSuccess: (view) => {
      setDone(view);
      form.setValue('text', '');
      preview.reset();
      void queryClient.invalidateQueries({ queryKey: msgKeys.bulk(session?.tenantId) });
    },
  });

  const columns: DataTableColumn<BulkNoticeView>[] = [
    {
      id: 'sent',
      headerKey: 'msg:bulk.columns.sent',
      cell: (row) => <JalaliDate value={row.sentAt} withTime />,
    },
    {
      id: 'range',
      headerKey: 'msg:bulk.columns.range',
      cell: (row) => (
        <span>
          <JalaliDate value={row.from} withTime /> — <JalaliDate value={row.to} withTime />
        </span>
      ),
    },
    {
      id: 'recipients',
      headerKey: 'msg:bulk.columns.recipients',
      cell: (row) => faDigits(String(row.recipientCount)),
    },
    { id: 'queued', headerKey: 'msg:bulk.columns.queued', cell: (row) => faDigits(String(row.queuedCount)) },
    {
      id: 'suppressed',
      headerKey: 'msg:bulk.columns.suppressed',
      cell: (row) => faDigits(String(row.suppressedCount)),
    },
    { id: 'by', headerKey: 'msg:bulk.columns.by', cell: (row) => row.createdByName },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_955}
      titleKey="msg:bulk.title"
      subtitleKey="msg:bulk.subtitle"
      filters={
        <Stack spacing={2}>
          <WriteNotice write={write} />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Alert severity="info">{t('msg:bulk.hint')}</Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <JalaliDatePickerRHF control={form.control} name="date" labelKey="msg:bulk.date" />
                <TextFieldRHF
                  control={form.control}
                  name="from"
                  labelKey="msg:bulk.from"
                  slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bulk-from' } }}
                />
                <TextFieldRHF
                  control={form.control}
                  name="to"
                  labelKey="msg:bulk.to"
                  slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bulk-to' } }}
                />
              </Stack>
              <TextFieldRHF
                control={form.control}
                name="text"
                labelKey="msg:bulk.text"
                multiline
                minRows={3}
                slotProps={{ htmlInput: { maxLength: 330, 'data-testid': 'bulk-text' } }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('msg:manual.length', { count: faDigits(text.length) })}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={() => preview.mutate()}
                  disabled={!write.canWrite || preview.isPending}
                  data-testid="bulk-preview"
                >
                  {t('msg:bulk.preview')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CampaignOutlinedIcon />}
                  onClick={() => send.mutate()}
                  disabled={
                    !write.canWrite ||
                    send.isPending ||
                    !preview.data ||
                    Number(preview.data.recipients) === 0 ||
                    text.trim().length === 0
                  }
                  data-testid="bulk-send"
                >
                  {t('msg:bulk.send')}
                </Button>
                {preview.data ? (
                  <Typography
                    data-testid="bulk-preview-result"
                    data-recipients={String(preview.data.recipients)}
                  >
                    {t('msg:bulk.preview_result', {
                      recipients: faDigits(String(preview.data.recipients)),
                      appointments: faDigits(String(preview.data.appointments)),
                      parts: faDigits(String(preview.data.length?.parts ?? 0)),
                    })}
                  </Typography>
                ) : null}
              </Stack>
              {preview.error ? <ErrorState error={preview.error} compact /> : null}
              {send.error ? <ErrorState error={send.error} compact /> : null}
              {done ? (
                <Alert severity="success" onClose={() => setDone(null)} data-testid="bulk-sent">
                  {t('msg:bulk.sent', {
                    queued: faDigits(String(done.queuedCount)),
                    suppressed: faDigits(String(done.suppressedCount)),
                  })}
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      }
    >
      <DataTable
        captionKey="msg:bulk.caption"
        columns={columns}
        rows={notices.data?.items}
        getRowId={(row) => row.id}
        isLoading={notices.isPending}
        error={notices.error}
        onRetry={() => void notices.refetch()}
        emptyTitleKey="msg:bulk.empty"
      />
    </ListPage>
  );
}
